import { useRelationshipQuery } from "Api/Queries/Social/useRelationshipQuery";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import type {
  PublicProfileDto,
  SocialRelationshipDto,
} from "Infrastructure/Api/Api";
import { canRoleBeFollowed, canRoleFollow } from "Utils/Social/socialRules";

type Profile = Pick<
  PublicProfileDto,
  "identityUserPublicID" | "handle" | "accessRoleCode"
>;

export type SocialPermissions = {
  /**
   * The current user may start following this persona — instantly, or by a follow request for a
   * private Customer (`relationship.requiresFollowApproval`). Also true for an anonymous visitor
   * whenever a signed-in customer could — the page keeps `!user` on `disabled`, so the button
   * renders as a placeholder.
   */
  canFollow: boolean;
  /**
   * Signed in, but what the buttons depend on is still loading (the caller's handle, or — for
   * a handle holder — the relationship). Buttons render disabled meanwhile, so nothing pops in
   * after the round trip.
   */
  isLoading: boolean;
  /** The caller has no handle yet — an action click must open the HandleGate instead. */
  needsHandle: boolean;
  /** The viewed persona is the caller's own (active or another persona) — render nothing. */
  isSelf: boolean;
  /** Server-computed relationship; undefined for anonymous visitors and while loading. */
  relationship: SocialRelationshipDto | undefined;
};

/**
 * Can the current user follow the viewed persona, plus the state the public profile needs to
 * render its social buttons. Rules (Rules v2, K2/K3, mirrored from the backend): only Customer
 * personas follow; Customers and Merchants can be followed, Hunters can't — private Customers by
 * request. Once the relationship is loaded, the server flags decide (blocks, existing edges,
 * pending requests).
 */
export const useSocialPermissions = (profile: Profile): SocialPermissions => {
  const user = useLoggedInUser();
  // The caller's handle comes from React Query user-info — the Redux user loses it on SPA
  // sign-in (SignInResult carries no handle). Skipped for anonymous visitors.
  const { data: userInfo } = useUserInfoQuery(!!user);

  // Catches the viewer's active persona before the relationship loads; relationship.isSelf
  // catches their OTHER personas (same Identity, server-detected).
  const isOwnHandle = !!userInfo?.handle && userInfo.handle === profile.handle;
  const { data: relationship } = useRelationshipQuery(
    user && !isOwnHandle ? profile.identityUserPublicID : undefined,
  );

  const isSelf = isOwnHandle || !!relationship?.isSelf;
  // Asserted only AFTER user-info loads — a still-loading handle must not flash the gate.
  const needsHandle = !!userInfo && !userInfo.handle;
  // A handle-less caller waits for nothing: the gate is the answer, whatever the server says.
  const isLoading =
    !!user && !isSelf && (!userInfo || (!needsHandle && !relationship));

  // Caller side. An anonymous visitor's role is unknown — optimistic, the placeholder is disabled.
  const isCallerAllowed = !user || canRoleFollow(user.accessRoleCode);
  // Server side, once known. Unknown (anonymous, loading) is optimistic — `!user` and
  // `isLoading` hold the button disabled. A handle-less caller is optimistic too: the server
  // reports false for the missing handle and the HandleGate intercepts the click.
  const serverAllowsFollow =
    needsHandle || !relationship || relationship.canFollow;

  return {
    canFollow:
      isCallerAllowed &&
      canRoleBeFollowed(profile.accessRoleCode) &&
      serverAllowsFollow,
    isLoading,
    needsHandle,
    isSelf,
    relationship,
  };
};
